import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import type { DetailActionStrict, ActionVariant } from "../../types/detail-action";
import { ACTION_ICONS, ACTION_VARIANTS, ROLE_COMBINATIONS } from "../../types/action-properties";

export function createAddUpdateAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
    primary?: boolean;
  } = {}
): DetailActionStrict<T> {
  return {
    id: "add-update",
    label: options.label ?? "Add Update",
    icon: options.icon ?? ACTION_ICONS.PLUS,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "addUpdate",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
    primary: options.primary ?? false,
  };
}

export function createAssignTicketAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "assign-ticket",
    label: options.label ?? "Assign",
    icon: options.icon ?? ACTION_ICONS.USER,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "assignTicket",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
  };
}

export function createAssignTicketToMeAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
    bulkable?: boolean;
  } = {}
): DetailActionStrict<T> {
  return {
    id: "assign-ticket-to-me",
    label: options.label ?? "Assign to me",
    icon: options.icon ?? ACTION_ICONS.USER,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "assignTicketToMe",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: options.bulkable ?? true,
    tooltip: "Assign this ticket to yourself.",
  };
}

export function createCloseTicketAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "close-ticket",
    label: options.label ?? "Close",
    icon: options.icon ?? ACTION_ICONS.X_CIRCLE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "closeTicket",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
  };
}

/**
 * "Acknowledge receipt" — emails the ticket requester a branded HTML
 * confirmation that we received the ticket (assignee, ticket #, support
 * channels, Hub link). Opens the mounted SendTicketCustomerEmailModal, which
 * POSTs to /api/tickets/[id]/customer-email. Customer-facing comms, so allowed
 * for employees + admins (mirrors the Slack card affordance).
 */
export function createSendTicketAckEmailAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "send-ticket-ack-email",
    label: options.label ?? "Acknowledge receipt",
    icon: options.icon ?? "mail",
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "sendTicketAckEmail",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip: "Email the requester that we received this ticket.",
  };
}

/**
 * "Send resolution email" — emails the requester a branded HTML confirmation
 * that the ticket is resolved. Opens the mounted SendTicketCustomerEmailModal.
 */
export function createSendTicketResolvedEmailAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "send-ticket-resolved-email",
    label: options.label ?? "Send resolution email",
    icon: options.icon ?? "check-circle",
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "sendTicketResolvedEmail",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip: "Email the requester that this ticket has been resolved.",
  };
}

/**
 * "Quick reply" — send the requester a customer-service email from the actor's
 * own mailbox (7 canned IT-support templates + an editable body), mirroring the
 * Slack ticket quick-reply modal. Opens the mounted SendTicketCustomerEmailModal
 * (kind "quick_reply"), which POSTs to /api/tickets/[id]/quick-reply. The
 * highest-value web parity gap with the Slack ticket card.
 */
export function createSendTicketQuickReplyAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "send-ticket-quick-reply",
    label: options.label ?? "Quick reply",
    icon: options.icon ?? "mail",
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "sendTicketQuickReply",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip: "Reply to the requester with a canned customer-service email.",
  };
}

export function createAddCommentAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "add-comment",
    label: options.label ?? "Add Comment",
    icon: options.icon ?? ACTION_ICONS.MESSAGE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "addComment",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
  };
}

export function createExportAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "export",
    label: options.label ?? "Export",
    icon: options.icon ?? ACTION_ICONS.DOWNLOAD,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "exportItem",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
  };
}
